import type { AuthChangeEvent, Session, Subscription } from '@supabase/supabase-js'

/** The reset form: `?reset=1` lets a recovery session set a password without the current one. */
export const PASSWORD_RESET_PATH = '/settings/account?reset=1'

/**
 * Where a recovery sign-in should go from `location`, or null when it is
 * already on the reset form. `/account?reset=1` (the address the in-app
 * "Forgot password" form asks for) forwards there on the server.
 */
export function passwordRecoveryTarget(location: Pick<Location, 'pathname' | 'search'>): string | null {
  const onResetForm =
    (location.pathname === '/settings/account' || location.pathname === '/account') &&
    new URLSearchParams(location.search).get('reset') === '1'
  return onResetForm ? null : PASSWORD_RESET_PATH
}

type OnAuthStateChange = (callback: (event: AuthChangeEvent, session: Session | null) => void) => {
  data: { subscription: Subscription }
}

/**
 * Send every recovery sign-in to the reset form, wherever the link landed.
 *
 * A reset link signs the customer in on whatever page Supabase redirected to:
 * the Supabase dashboard's "Send password recovery" and any redirect the allow
 * list rejects both land on the site root, where routing sends a new account to
 * onboarding and nothing offers a new password. Supabase announces the sign-in
 * once, as `PASSWORD_RECOVERY`, right after it reads the link, before the
 * app's own auth listener subscribes, so this subscribes as the client is
 * created.
 */
export function watchPasswordRecovery(
  onAuthStateChange: OnAuthStateChange,
  navigate: (url: string) => void = url => window.location.replace(url),
  getLocation: () => Pick<Location, 'pathname' | 'search'> = () => window.location,
): Subscription {
  const {
    data: { subscription },
  } = onAuthStateChange(event => {
    if (event !== 'PASSWORD_RECOVERY') return
    const target = passwordRecoveryTarget(getLocation())
    if (target) navigate(target)
  })
  return subscription
}
